import { zodResolver } from '@hookform/resolvers/zod';
import Collapse from '@material-ui/core/Collapse';
import { useSpring } from '@react-spring/web';
import { useAbbiamoUiConfig } from '@seller-hermes/app/config';
import SellersSelector from '@seller-hermes/features/seller/SellersSelector/sellers-selector.component';
import AlertDialog from '@seller-hermes/features/ui-v2/AlertDialog';
import SupportInfoPopup from '@seller-hermes/features/support-info-popup';
import { Button } from '@seller-hermes/features/ui-v2/Button';
import Checkbox from '@seller-hermes/features/ui-v2/Checkbox';
import Form from '@seller-hermes/features/ui-v2/Form';
import Input from '@seller-hermes/features/ui-v2/Input';
import Label from '@seller-hermes/features/ui-v2/Label';
import RadioGroup from '@seller-hermes/features/ui-v2/RadioGroup';
import Textarea from '@seller-hermes/features/ui-v2/Textarea';
import { UsersQuery } from '@seller-hermes/features/users/users.query';
import {
  useStoreActions,
  useStoreState,
} from '@seller-hermes/hooks/easy-peasy';
import { useThemeToggler } from '@seller-hermes/hooks/useThemeToggler';
import { HelmetIcon } from '@seller-hermes/public/icons/icons';
import { withRoutePlanningTheme } from '@seller-hermes/theme/hoc';
import * as easings from 'd3-ease';
import { addDays, addMonths } from 'date-fns';
import {
  Cable,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronsLeft,
  FileText,
  Globe,
  HelpCircle,
  LogOut,
  Map,
  Monitor,
  Moon,
  Package,
  PercentCircle,
  Settings,
  Settings2,
  Sheet,
  Star,
  Store,
  Sun,
  Zap,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useClickAway } from 'react-use';
import { toast } from 'sonner';
import { useTheme } from 'styled-components';
import { z } from 'zod';
import {
  FeatureFlag,
  getSellerGroupFeatureFlag,
  useSellerGroupFeatureFlag,
} from '../../../hooks/useSellerGroupFeatureFlag';
import Popover from '../Popover/popover.component';
import { FetchKeys } from '../constants';
import {
  Container,
  Divider,
  Icon,
  Links,
  Logo,
  LogoImg,
  LogoutIconButton,
  Menu,
  MenuItem,
  MenuItemText,
  MenuSubItemText,
  NavToggleIconButton,
  PopoverContent,
  Section,
  SelectorContainer,
  UserCard,
  UserCardInformation,
  UserEmail,
  UserName,
} from './vertical-nav-bar.styles';
import {
  SellerGroupUser,
  SellerGroupUserPermissions,
} from '@seller-hermes/features/seller-group';
import { productTracker } from '@seller-hermes/product-tracking';

const MAX_WIDTH = 30;
const MIN_WIDTH = 5.5;

const NavPopover: typeof Popover = ({ children, ...props }) => (
  <Popover
    {...props}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    style={{
      marginLeft: '0.7rem',
    }}
    PaperProps={{
      style: {
        borderRadius: '0.5rem',
        boxShadow: `0px 20px 24px -4px rgba(16, 24, 40, 0.08),
        0px 8px 8px -4px rgba(16, 24, 40, 0.03)`,
      },
    }}
  >
    {children}
  </Popover>
);

const SubItem = ({
  setActiveSubItems,
  isOpen,
  name,
  icon,
  links,
  activeSubItems,
  currentPage,
  changePage,
  index,
  onClick,
}) => {
  const value = index === activeSubItems ? true : false;

  const isSelected = links?.some((link) => link.to === currentPage);

  const linksElement = (
    <Links>
      {Array.isArray(links) &&
        links.map(({ name, to }) => (
          <MenuItem
            as="button"
            onClick={() => {
              changePage(to);
            }}
            key={name}
          >
            {!isOpen && <Icon>{icon}</Icon>}
            <MenuSubItemText>{name}</MenuSubItemText>
          </MenuItem>
        ))}
    </Links>
  );

  const itemRef = useRef(null);

  useClickAway(itemRef, () => {
    if (value) {
      setActiveSubItems(null);
    }
  }, ['click']);

  const handleOnClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (value) {
      setActiveSubItems(null);
      return;
    }

    setActiveSubItems(index);
  };

  return (
    <React.Fragment key={name}>
      <MenuItem
        ref={itemRef}
        key={name}
        onClick={handleOnClick}
        $focused={isSelected}
      >
        <Icon>{icon}</Icon>
        {isOpen && (
          <>
            <MenuItemText>{name}</MenuItemText>{' '}
            {!onClick && (
              <Icon style={{ transform: value ? 'rotateX(180deg)' : '' }}>
                <ChevronDown />
              </Icon>
            )}
          </>
        )}
      </MenuItem>
      {isOpen ? (
        <Collapse style={{ width: '100%' }} in={value}>
          {linksElement}
        </Collapse>
      ) : (
        <NavPopover isOpen={value} anchorEl={itemRef.current}>
          <PopoverContent>{linksElement}</PopoverContent>
        </NavPopover>
      )}
    </React.Fragment>
  );
};
const allowedUserViewTypes = ['ADMIN', 'MASTER', 'INTERNAL'];

interface VerticalNavBarPage {
  name: string;
  icon: React.ReactNode;
  feature_flag?: FeatureFlag;
  user_type?: string[];
  links?: {
    name: string;
    to: string;
    permissions: string[];
  }[];
  onClick?: () => void;
}

export const hasAnyPermission = (
  user: SellerGroupUser | null,
  permissions: any[],
) => {
  if (!user) return false;
  return permissions.some((permission) => user?.all_permissions?.[permission]);
};

export const hasProfilePermission = (
  user: SellerGroupUser | null,
  permissions: any[],
) => {
  if (!user) return false;
  return permissions.some(
    (permission) => user?.profile_permissions?.[permission],
  );
};

export const hasHiredModulesPermission = (
  user: SellerGroupUser | null,
  permissions: any[],
) => {
  if (!user) return false;
  return permissions.some(
    (permission) => user?.hired_modules_permissions?.[permission],
  );
};

const VerticalNavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { width } = useSpring({
    to: { width: isOpen ? MAX_WIDTH : MIN_WIDTH },
    from: { width: isOpen ? MIN_WIDTH : MAX_WIDTH },
    config: {
      duration: 500,
      easing: easings.easeExp,
    },
  });
  const [activeSubItems, setActiveSubItems] = useState<number | null>(null);
  const [activeFooterSubItems, setActiveFooterSubItems] = useState<
    number | null
  >(null);
  useEffect(() => {
    if (isOpen === false) {
      setActiveSubItems(null);
    }
  }, [isOpen, setActiveSubItems]);
  const {
    seller: { logo, favicon },
  } = useTheme();

  const { t } = useTranslation('vertical-nav-bar');

  const user = useStoreState((state) => state.login.sellerGroupUser.user);

  const {
    getMe,
    navbar: {
      changePage,
      currentPage,
      signoff,
      changeLanguage,
      currentLanguage,
      setSelectedSellers,
      selectedSellers,
    },
    role_name,
    permissions: user_permissions,
  } = useAbbiamoUiConfig();

  const { data: me } = useQuery([FetchKeys.ME], getMe, {
    refetchOnWindowFocus: false,
  });

  const { themePreference, setTheme } = useThemeToggler();

  // Dashboard novo: staff Abbiamo OU marca com a flag (espelha rota + sidebar).
  const isDashboardGenerativo = useSellerGroupFeatureFlag('dashboard-generativo');
  // Gate da tela ANTIGA (analytics), pra decidir quem vê a antiga aqui também.
  const isAnalyticsEnabled = useSellerGroupFeatureFlag('enable-analytics-page');

  const pages = useMemo(() => {
    const allPages: VerticalNavBarPage[] = [];

    // Função auxiliar para criar links condicionalmente baseado em permissões
    const createConditionalLinks = (
      permissionChecks: Array<{
        hasPermission: boolean;
        link: { name: string; to: string; permissions: string[] };
      }>,
    ) => {
      return permissionChecks
        .filter((check) => check.hasPermission)
        .map((check) => check.link);
    };

    // Espelha a barra em uso (`app/app-sidebar.tsx`): o Dashboard novo fica com
    // o nome ("Dashboard"), e o antigo continua alcançável marcado como tal.
    // LANÇAMENTO SOFT: staff Abbiamo (INTERNAL) OU marca com a flag
    // `dashboard-generativo` (espelha o guard da rota `/dashboard`). Gate por
    // TIPO/flag, não por permissão: INTERNAL pode não ter a permissão de pedido
    // no grupo, e aí um `hasAnyPermission` o esconderia sem querer. SEM
    // `user_type` (o filtro da linha ~590 barraria a marca não-INTERNAL que tem
    // a flag) — a condição do push já é o gate.
    const verNovoDashboard = me?.type === 'INTERNAL' || isDashboardGenerativo;
    if (verNovoDashboard) {
      allPages.push({
        name: t('analytics'),
        icon: <ChartNoAxesCombined />,
        onClick: () => {
          changePage('/dashboard');
        },
      });
    }

    // A tela ANTIGA (analytics). Gate DELA inalterado — permissão de dashboard +
    // flag `enable-analytics-page` — mais o bypass de INTERNAL (staff vê as
    // duas). SEM `user_type` fixo em INTERNAL: o cliente com o gate de analytics
    // precisa vê-la. Nome vira "(antigo)" só pra quem TAMBÉM vê a nova.
    if (
      me?.type === 'INTERNAL' ||
      (hasAnyPermission(user, [SellerGroupUserPermissions.DASHBOARD]) &&
        isAnalyticsEnabled)
    ) {
      allPages.push({
        name: verNovoDashboard ? `${t('analytics')} (antigo)` : t('analytics'),
        icon: <ChartNoAxesCombined />,
        onClick: () => {
          changePage('/analytics');
        },
      });
    }

    if (hasAnyPermission(user, [SellerGroupUserPermissions.ORDERS])) {
      allPages.push({
        name: t('orders'),
        icon: <Package />,
        links: [
          {
            name: t('list-orders'),
            to: '/',
            permissions: ['read_order'],
          },
          {
            name: t('create-order'),
            to: '/orders/create',
            permissions: ['read_order', 'create_order'],
          },
          me?.all_permissions?.['spot_to_carrier'] &&
            me?.all_permissions?.['crowdshipping'] && {
              name: t('create-delivery'),
              to: '/orders/request-pickup',
              permissions: ['read_delivery', 'create_delivery'],
            },
          {
            name: t('quote-freight'),
            to: '/orders/quote-freight',
            permissions: ['read_order', 'create_order'],
          },
        ].filter(Boolean) as {
          name: string;
          to: string;
          permissions: string[];
        }[],
      });
    }

    if (
      hasAnyPermission(user, [
        SellerGroupUserPermissions.ROUTES_TO_CARRIER,
        SellerGroupUserPermissions.ROUTES_TO_PRIVATE_FLEET,
      ])
    ) {
      allPages.push({
        name: t('routes'),
        icon: <Map />,
        links: [
          {
            name: t('list-routes'),
            to: '/routes',
            permissions: ['read_route'],
          },
          {
            name: t('create-route'),
            to: '/routes/create',
            permissions: ['create_route'],
          },
        ],
      });
    }

    // Adiciona página de drivers se a permissão existir
    if (hasAnyPermission(user, [SellerGroupUserPermissions.DRIVERS])) {
      allPages.push({
        name: t('drivers'),
        icon: <HelmetIcon />,
        links: [
          {
            name: t('list-drivers'),
            to: '/drivers',
            permissions: ['read_driver'],
          },
        ],
      });
    }

    // Adiciona página de automações se pelo menos uma permissão existir
    if (
      hasAnyPermission(user, [
        SellerGroupUserPermissions.DISPATCH_RULE_TO_CARRIER,
        SellerGroupUserPermissions.RESEND_RULE_TO_CARRIER,
        SellerGroupUserPermissions.INACTIVITY_RULE_TO_CARRIER,
      ])
    ) {
      const automationLinks = createConditionalLinks([
        {
          hasPermission: !!me?.all_permissions?.dispatch_rule_to_carrier,
          link: {
            name: t('dispatch-rules'),
            to: '/dispatch-rules',
            permissions: ['read_dispatch_rules'],
          },
        },
        {
          hasPermission: !!me?.all_permissions?.resend_rule_to_carrier,
          link: {
            name: t('resend-rules'),
            to: '/resend-rules',
            permissions: ['read_resend_rules'],
          },
        },
        {
          hasPermission: !!me?.all_permissions?.inactivity_rule_to_carrier,
          link: {
            name: t('inactivity-rules'),
            to: '/inactivity-rules',
            permissions: ['read_inactivity_rules'],
          },
        },
        {
          hasPermission: true,
          link: {
            name: t('tags-rules'),
            to: '/tags-rules',
            permissions: [],
          },
        },
      ]);

      if (automationLinks.length > 0) {
        allPages.push({
          name: t('automations'),
          icon: <Zap />,
          links: automationLinks,
        });
      }
    }

    // Adiciona página de tabelas se a permissão existir
    if (hasAnyPermission(user, [SellerGroupUserPermissions.FREIGHT_TABLES])) {
      allPages.push({
        name: t('tables'),
        icon: <Sheet />,
        links: [
          {
            name: t('shipping-tables-radius'),
            to: '/shipping-tables/radius',
            permissions: ['read_order'],
          },
          {
            name: t('shipping-tables-cep'),
            to: '/shipping-tables/cep',
            permissions: ['read_order'],
          },
        ],
      });
    }

    // Mostra as páginas de cotações se o profile tiver permissão, mesmo que não tenha contratado (será bloqueado por paywall dentro da página)
    if (
      hasProfilePermission(user, [
        SellerGroupUserPermissions.QUOTATIONS,
        SellerGroupUserPermissions.QUOTATION_RULES,
      ])
    ) {
      allPages.push({
        name: t('quotations'),
        icon: <PercentCircle />,
        links: [
          {
            name: t('quotations'),
            to: '/quotations',
            permissions: [],
          },
          {
            name: t('quotation-rules'),
            to: '/quotation-rules',
            permissions: ['read_dispatch_rules'],
          },
        ],
      });
    }

    // Adiciona página de vendedores se a permissão existir
    if (hasAnyPermission(user, [SellerGroupUserPermissions.SELLERS])) {
      allPages.push({
        name: t('sellers'),
        icon: <Store />,
        links: [
          {
            name: t('list-sellers'),
            to: '/sellers',
            permissions: ['read_sellers'],
          },
        ],
      });
    }

    // Adiciona página de reviews se a permissão existir
    if (hasAnyPermission(user, [SellerGroupUserPermissions.CSAT])) {
      allPages.push({
        name: t('csat'),
        icon: <Star />,
        links: [
          {
            name: t('list-csat'),
            to: '/csat',
            permissions: ['read_nps'],
          },
        ],
      });
    }

    // Adiciona página de integrações se pelo menos uma permissão existir
    if (
      hasAnyPermission(user, [
        SellerGroupUserPermissions.CARRIER_INTEGRATIONS,
        SellerGroupUserPermissions.ORDER_INTEGRATIONS,
      ])
    ) {
      const integrationLinks = createConditionalLinks([
        {
          hasPermission: !!me?.all_permissions?.order_integrations,
          link: {
            name: t('list-order-integrations'),
            to: '/order-integrations',
            permissions: ['read_order_integrations'],
          },
        },
        {
          hasPermission: !!me?.all_permissions?.carrier_integrations,
          link: {
            name: t('list-carrier-integrations'),
            to: '/carrier-integrations',
            permissions: ['read_carrier_integrations'],
          },
        },
      ]);

      if (integrationLinks.length > 0) {
        allPages.push({
          name: t('integrations'),
          icon: <Cable />,
          links: integrationLinks,
        });
      }
    }

    // Adiciona página de relatórios se a permissão existir
    if (hasAnyPermission(user, [SellerGroupUserPermissions.REPORTS])) {
      allPages.push({
        name: t('reports'),
        icon: <FileText />,
        links: [
          {
            name: t('list-reports'),
            to: '/reports',
            permissions: ['read_report'],
          },
        ],
      });
    }

    const pagesByFeatureFlag = allPages.filter((page) =>
      page.feature_flag
        ? getSellerGroupFeatureFlag(page.feature_flag, user)
        : true,
    );

    const pagesByUserType = pagesByFeatureFlag.filter((page) =>
      page.user_type ? page.user_type.includes(me?.type || '') : true,
    );

    return pagesByUserType;
  }, [
    changePage,
    me?.type,
    t,
    user,
    me?.all_permissions,
    isDashboardGenerativo,
    isAnalyticsEnabled,
  ]);

  const footer: {
    onClick?: any;
    name: string;
    icon: React.ReactNode;
    subItems?: { onClick?: any; name: string; icon: React.ReactNode }[];
  }[] = useMemo(
    () => [
      {
        name: t('configurations'),
        icon: <Settings />,
        subItems: [
          {
            onClick: () => setTheme('light'),
            name: `${t('light-theme')}${
              themePreference === 'light' ? ' ✓' : ''
            }`,
            icon: <Sun />,
          },
          {
            onClick: () => setTheme('dark'),
            name: `${t('dark-theme')}${
              themePreference === 'dark' ? ' ✓' : ''
            }`,
            icon: <Moon />,
          },
          {
            onClick: () => setTheme('system'),
            name: `${t('system-theme')}${
              themePreference === 'system' ? ' ✓' : ''
            }`,
            icon: <Monitor />,
          },
          {
            name: `${t('change-language-to')}: ${t(
              `language.${currentLanguage === 'pt-BR' ? 'en' : 'pt-BR'}`,
            )}`,
            onClick: () => {
              changeLanguage(currentLanguage === 'pt-BR' ? 'en' : 'pt-BR');
            },
            icon: <Globe />,
          },
          ...(me && allowedUserViewTypes.includes(me.type)
            ? [
                {
                  name: t('group-settings'),
                  onClick: () => {
                    changePage('/settings');
                  },
                  icon: <Settings />,
                },
              ]
            : []),
          {
            name: t('preferences'),
            onClick: () => {
              changePage('/preferences');
            },
            icon: <Settings2 />,
          },
        ],
      },
      {
        name: t('help-center'),
        icon: <HelpCircle />,
        onClick: () => {
          productTracker.track('Open Support Widget');
          changePage('/support/tickets');
        },
      },
    ],
    [
      t,
      themePreference,
      setTheme,
      currentLanguage,
      me,
      changeLanguage,
      changePage,
    ],
  );

  const pagesFilteredByPermission = useMemo(
    () =>
      pages
        .map((page) => {
          const links = page?.links?.filter((link) => {
            if (allowedUserViewTypes?.includes(me?.type || '')) return true;
            return link?.permissions?.every(
              (permission) => user_permissions[permission],
            );
          });
          return {
            ...page,
            links,
          };
        })
        .filter((page) => (page?.links?.length || 0) > 0 || page?.onClick),
    [me?.type, pages, user_permissions],
  );

  return (
    <Container
      style={{ width: width.to((v) => `${v}rem`) }}
      className="border-r"
    >
      <Section>
        <NavToggleIconButton
          onClick={() => setIsOpen((v) => !v)}
          style={{ transform: !isOpen ? 'rotateZ(180deg)' : '' }}
        >
          <ChevronsLeft />
        </NavToggleIconButton>
        <Logo
          onClick={() => {
            changePage('/');
          }}
          style={{
            paddingLeft: width.to([MIN_WIDTH, MAX_WIDTH], ['0.5rem', '2rem']),
            paddingRight: width.to([MIN_WIDTH, MAX_WIDTH], ['0.5rem', '2rem']),
          }}
        >
          <LogoImg src={isOpen ? logo : favicon} />
        </Logo>
        <Menu>
          {pagesFilteredByPermission.map(
            ({ icon, links, name, onClick }, index) => (
              <SubItem
                key={index}
                activeSubItems={activeSubItems}
                changePage={changePage}
                currentPage={currentPage}
                icon={icon}
                links={links}
                name={name}
                setActiveSubItems={setActiveSubItems}
                isOpen={isOpen}
                index={index}
                onClick={onClick}
              />
            ),
          )}
        </Menu>
      </Section>
      <Section>
        <SelectorContainer
          style={{
            opacity: width.to((v) =>
              [MIN_WIDTH, MAX_WIDTH].includes(v) ? 1 : 0,
            ),
            display: width.to((v) =>
              [MIN_WIDTH, MAX_WIDTH].includes(v) ? 'unset' : 'none',
            ),
          }}
        >
          <SellersSelector
            onSelectedSellersChange={setSelectedSellers}
            selectedSellers={selectedSellers}
            onlyShowLength={!isOpen}
            minimumAllowedSelectedSellers={1}
          />
        </SelectorContainer>
        <Divider />
        <Menu>
          {footer.map(({ name, icon, onClick, subItems }, index) => {
            const value = index === activeFooterSubItems ? true : false;
            const linksElement = (
              <Links>
                {Array.isArray(subItems) &&
                  subItems.map(({ name, onClick, icon }) => (
                    <MenuItem as="button" onClick={onClick} key={name}>
                      <Icon>{icon}</Icon>
                      <MenuSubItemText>{name}</MenuSubItemText>
                    </MenuItem>
                  ))}
              </Links>
            );
            const itemRef = useRef(null);
            useClickAway(itemRef, () => {
              if (value) {
                setActiveFooterSubItems(null);
              }
            }, ['click']);
            return (
              <React.Fragment key={name}>
                <MenuItem
                  onClick={() => {
                    if (onClick) {
                      onClick();
                    } else {
                      value
                        ? setActiveFooterSubItems(null)
                        : setActiveFooterSubItems(index);
                    }
                  }}
                  ref={itemRef}
                  key={name}
                >
                  <Icon>{icon}</Icon>
                  {isOpen && <MenuItemText>{name}</MenuItemText>}
                  {isOpen && !onClick && Array.isArray(subItems) && (
                    <Icon style={{ transform: value ? 'rotateX(180deg)' : '' }}>
                      <ChevronDown />
                    </Icon>
                  )}
                </MenuItem>
                {isOpen ? (
                  <Collapse style={{ width: '100%' }} in={value}>
                    {linksElement}
                  </Collapse>
                ) : (
                  <NavPopover isOpen={value} anchorEl={itemRef.current}>
                    <PopoverContent>{linksElement}</PopoverContent>
                  </NavPopover>
                )}
              </React.Fragment>
            );
          })}
        </Menu>
        <UserCard style={{ marginTop: '1rem' }}>
          <UserCardInformation>
            {isOpen && (
              <>
                <UserName>
                  {' '}
                  {t(`user-role.${me?.type}`)}{' '}
                  {me?.type === 'USER' ? `(${t(`role.${role_name}`)})` : ''}
                </UserName>
                <UserEmail>{me?.email}</UserEmail>
              </>
            )}
          </UserCardInformation>
          <LogoutIconButton onClick={() => signoff()}>
            <LogOut />
          </LogoutIconButton>
        </UserCard>
      </Section>

      <UserFeedbackDialog />
      <SupportInfoPopup />
    </Container>
  );
};

export default withRoutePlanningTheme(VerticalNavBar);

const feedbackFormSchema = z.object({
  rating: z.string().min(1, 'Dê uma avaliação'),
  feedback: z.string().min(1, 'Deixe seu feedback').optional(),
  job_title: z.string().min(1, 'Informe seu cargo'),
  stores_managed: z.string().min(1, 'Informe quantas lojas você gerencia'),
  subscribe: z.boolean().default(true),
  subscribe_email: z.string().min(1, 'Informe seu email').optional(),
});

type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

export function UserFeedbackDialog() {
  const user = useStoreState((state) => state.login.sellerGroupUser.user);

  const { feedbackDialogOpen, feedbackDialogShouldOpenAgainAt } = useStoreState(
    (state) => state.userFeedbackDialog,
  );
  const { setFeedbackDialogOpen, setFeedbackDialogShouldOpenAgainAt } =
    useStoreActions((actions) => actions.userFeedbackDialog);
  const { setSupportInfoDialogShouldOpenAgainAt } = useStoreActions(
    (actions) => actions.supportInfoPopup,
  );

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: {
      job_title: '',
      stores_managed: '',
      rating: '',
      feedback: '',
      subscribe: true,
      subscribe_email: user?.email,
    },
  });

  const {
    mutateAsync: sendUserFeedback,
    isLoading: isLoadingSendUserFeedback,
  } = useMutation({
    mutationFn: async (data: FeedbackFormValues) => {
      return await UsersQuery.sendFeedback(data);
    },
    onSuccess: () => {
      // closes dialog and set to reopen in 3 months
      setFeedbackDialogOpen(false);
      setFeedbackDialogShouldOpenAgainAt(
        addMonths(new Date(), 3).toISOString(),
      );
    },
  });

  function onSubmit(data: FeedbackFormValues) {
    // TODO: Implement form submission
    toast.promise(sendUserFeedback(data), {
      loading: 'Enviando feedback...',
      success: 'Feedback enviado com sucesso!',
      error: 'Erro ao enviar feedback',
    });
  }

  const storesManagedOptions = [
    {
      value: '1',
      label: '1',
    },
    {
      value: '2-10',
      label: '2 - 10',
    },
    {
      value: '11-50',
      label: '11 - 50',
    },
    {
      value: '50+',
      label: '50 +',
    },
  ];

  const shouldShowFeedbackDialog = useMemo(() => {
    if (!feedbackDialogOpen) return false;
    // if user is newer than 3 months, it should not open
    if (user?.created_at) {
      return addMonths(new Date(user.created_at), 3) < new Date();
    }
    // if today is after the date in feedbackDialogWasClosed, it should open
    if (feedbackDialogShouldOpenAgainAt) {
      return new Date(feedbackDialogShouldOpenAgainAt) < new Date();
    }
    return true;
  }, [feedbackDialogOpen, feedbackDialogShouldOpenAgainAt, user?.created_at]);

  // When feedback is shown (opens), schedule support info popup to only open one day later
  useEffect(() => {
    if (shouldShowFeedbackDialog) {
      setSupportInfoDialogShouldOpenAgainAt(
        addDays(new Date(), 1).toISOString(),
      );
    }
  }, [shouldShowFeedbackDialog, setSupportInfoDialogShouldOpenAgainAt]);

  return (
    <AlertDialog
      open={shouldShowFeedbackDialog}
      onOpenChange={setFeedbackDialogOpen}
    >
      <AlertDialog.Content className="max-w-[50%]">
        <AlertDialog.Header>
          <AlertDialog.Title className="flex items-center gap-x-2">
            Nos ajude a melhorar!
          </AlertDialog.Title>
        </AlertDialog.Header>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-y-4 pb-4">
              <div className="flex w-full gap-x-4">
                <Form.Field
                  control={form.control}
                  name="job_title"
                  render={({ field }) => (
                    <Form.Item className="w-full">
                      <Form.Label
                        htmlFor="job_title"
                        required
                        disabled={isLoadingSendUserFeedback}
                      >
                        Qual o seu cargo?
                      </Form.Label>
                      <Form.Control>
                        <Input
                          {...field}
                          placeholder="Gerente de loja"
                          disabled={isLoadingSendUserFeedback}
                        />
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />
                <Form.Field
                  control={form.control}
                  name="stores_managed"
                  render={({ field }) => (
                    <Form.Item className="w-full">
                      <Form.Label
                        htmlFor="job_title"
                        required
                        disabled={isLoadingSendUserFeedback}
                      >
                        Quantas lojas você gerencia?
                      </Form.Label>
                      <Form.Control>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex gap-2"
                          disabled={isLoadingSendUserFeedback}
                        >
                          {storesManagedOptions.map((option) => (
                            <div className="[&:has(:checked)]:border-foreground relative flex w-full items-start gap-2 rounded-md border p-2.5 outline-none">
                              <RadioGroup.Item
                                value={option.value}
                                id={option.value}
                                aria-describedby={`${option.value}-description`}
                                className="after:absolute after:inset-0"
                              />
                              <Label
                                htmlFor={option.value}
                                className="whitespace-nowrap"
                              >
                                {option.label}
                              </Label>
                            </div>
                          ))}
                        </RadioGroup>
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />
              </div>

              <Form.Field
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Label
                      htmlFor="rating"
                      required
                      disabled={isLoadingSendUserFeedback}
                    >
                      Como você avalia a experiência com a plataforma?
                    </Form.Label>
                    <Form.Control>
                      <div>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex gap-0 -space-x-px rounded-md"
                          disabled={isLoadingSendUserFeedback}
                        >
                          {[
                            '0',
                            '1',
                            '2',
                            '3',
                            '4',
                            '5',
                            '6',
                            '7',
                            '8',
                            '9',
                            '10',
                          ].map((value) => (
                            <label
                              key={value}
                              className="border-input [&:has(:checked)]:bg-foreground [&:has(:checked)]:text-background [&:has(:checked)]:border-foreground has-focus-visible:border-ring has-focus-visible:ring-ring/50 has-focus-visible:ring-[3px] has-data-disabled:cursor-not-allowed has-data-disabled:opacity-50 relative flex h-9 flex-1 cursor-pointer flex-col items-center justify-center gap-3 border text-center text-sm font-medium outline-none transition-[color,box-shadow] first:rounded-s-md last:rounded-e-md [&:has(:checked)]:z-10"
                            >
                              <RadioGroup.Item
                                id={`rating-${value}`}
                                value={value}
                                className="sr-only after:absolute after:inset-0"
                              />
                              {value}
                            </label>
                          ))}
                        </RadioGroup>
                        <div className="text-muted-foreground mt-1 flex justify-between text-xs">
                          <div className="flex items-center gap-x-1">
                            <span
                              className="text-base"
                              role="img"
                              aria-label="Cara triste"
                            >
                              😡
                            </span>
                            <span>Não gosto</span>
                          </div>
                          <div className="flex items-center gap-x-1">
                            <span>Gosto bastante</span>
                            <span
                              className="text-base"
                              role="img"
                              aria-label="Cara feliz"
                            >
                              😍
                            </span>
                          </div>
                        </div>
                      </div>
                    </Form.Control>
                    <Form.Message />
                  </Form.Item>
                )}
              />

              <Form.Field
                control={form.control}
                name="feedback"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Label
                      htmlFor="feedback"
                      required
                      disabled={isLoadingSendUserFeedback}
                    >
                      Conte-nos o que você gostaria de ver na plataforma ou como
                      podemos melhorar o nosso produto
                    </Form.Label>
                    <Form.Control>
                      <Textarea
                        {...field}
                        placeholder="Deixe seu feedback"
                        disabled={isLoadingSendUserFeedback}
                      />
                    </Form.Control>
                    <Form.Message />
                  </Form.Item>
                )}
              />

              <div className="flex flex-col gap-y-2 rounded-md border border-dashed p-4">
                <Form.Field
                  control={form.control}
                  name="subscribe"
                  render={({ field }) => (
                    <div className="flex flex-col gap-y-2">
                      <Form.Item className="flex-row items-center gap-2">
                        <Form.Control>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </Form.Control>
                        <Form.Label>
                          Quero me inscrever para receber novidades sobre o
                          produto
                        </Form.Label>
                      </Form.Item>
                      <Form.Label className="text-muted-foreground ml-7 font-normal">
                        Eu aceito ser contatado pelo time de produto para saber
                        mais sobre novas funcionalidades e ajudar a melhorar o
                        produto.
                      </Form.Label>
                    </div>
                  )}
                />

                <Form.Field
                  control={form.control}
                  name="subscribe_email"
                  render={({ field }) => (
                    <Form.Item className="ml-7">
                      <Form.Control>
                        <Input
                          {...field}
                          placeholder="Deixe seu feedback"
                          disabled={isLoadingSendUserFeedback}
                        />
                      </Form.Control>
                    </Form.Item>
                  )}
                />
              </div>
            </div>

            <AlertDialog.Footer>
              <div className="flex w-full justify-between gap-x-2">
                <Button
                  variant="link"
                  onClick={() => {
                    // closes dialog and set to reopen in 3 months
                    setFeedbackDialogOpen(false);
                    setFeedbackDialogShouldOpenAgainAt(
                      addMonths(new Date(), 3).toISOString(),
                    );
                  }}
                  type="button"
                  className="text-muted-foreground hover:text-foreground underline"
                  disabled={isLoadingSendUserFeedback}
                >
                  Não perguntar novamente
                </Button>

                <div className="flex gap-x-2">
                  <Button
                    variant="outline"
                    disabled={isLoadingSendUserFeedback}
                    onClick={() => {
                      // closes dialog and set to reopen in 2 days
                      setFeedbackDialogOpen(false);
                      setFeedbackDialogShouldOpenAgainAt(
                        addDays(new Date(), 2).toISOString(),
                      );
                    }}
                    type="button"
                  >
                    Responder mais tarde
                  </Button>
                  <Button type="submit" disabled={isLoadingSendUserFeedback}>
                    Enviar feedback
                  </Button>
                </div>
              </div>
            </AlertDialog.Footer>
          </form>
        </Form>
      </AlertDialog.Content>
    </AlertDialog>
  );
}
